'use client';

import React, { useState, useMemo, useCallback, useRef } from 'react';
import {
  CheckCircle, AlertTriangle, XCircle, Cpu, Activity, Zap,
  ChevronDown, ChevronUp, Info, RefreshCw, BarChart2, Layers,
  Upload, GitBranch, Radio,
} from 'lucide-react';
import { CANONICAL_CHANNELS, DeviceType, StandardisedEEGInput, MappedChannel, CompatibilityReport, BiomarkerAdaptation, CoherencePair, mapChannelsToCanonical, validateCompatibility, computeBiomarkerAdaptations, computeCoherencePairs, buildAIChannelContext, buildMultimodalSyncStatus, TEST_DATASETS, EEGRegion, normaliseLabel,  } from '../utils/channelAlignment';
import EEGTopoMap from './EEGTopoMap';

// ─── Sub-components ───────────────────────────────────────────────────────────

function ScoreBadge({ score }: { score: number }) {
  const color = score >= 80 ? 'text-emerald-400' : score >= 55 ? 'text-amber-400' : 'text-red-400';
  const bg = score >= 80 ? 'bg-emerald-950/40 border-emerald-800/50' : score >= 55 ? 'bg-amber-950/40 border-amber-800/50' : 'bg-red-950/40 border-red-800/50';
  const label = score >= 80 ? 'Excellent' : score >= 55 ? 'Moderate' : 'Limited';
  return (
    <div className={`flex flex-col items-center justify-center w-20 h-20 rounded-full border-2 ${bg}`}>
      <span className={`text-2xl font-bold font-mono ${color}`}>{score}</span>
      <span className={`text-[10px] font-semibold uppercase tracking-wide ${color}`}>{label}</span>
    </div>
  );
}

function ReliabilityBadge({ reliability }: { reliability: CompatibilityReport['biomarkerReliability'] }) {
  const map = {
    high:     { color: 'text-emerald-400', bg: 'bg-emerald-950/40 border-emerald-800/50', icon: <CheckCircle size={12} /> },
    moderate: { color: 'text-amber-400',   bg: 'bg-amber-950/40 border-amber-800/50',     icon: <AlertTriangle size={12} /> },
    limited:  { color: 'text-red-400',     bg: 'bg-red-950/40 border-red-800/50',         icon: <XCircle size={12} /> },
  };
  const s = map[reliability];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${s.bg} ${s.color}`}>
      {s.icon}
      {reliability.charAt(0).toUpperCase() + reliability.slice(1)} reliability
    </span>
  );
}

function CoverageBar({ region, coverage }: { region: string; coverage: number }) {
  const pct = Math.round(coverage * 100);
  const color = pct >= 75 ? 'bg-emerald-400' : pct >= 40 ? 'bg-amber-400' : 'bg-red-400';
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] text-zinc-400 w-20 capitalize">{region}</span>
      <div className="flex-1 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-700 ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[11px] font-mono text-zinc-400 w-8 text-right">{pct}%</span>
    </div>
  );
}

function BiomarkerRow({ bm }: { bm: BiomarkerAdaptation }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border border-zinc-800/50 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-zinc-800/30 transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
            !bm.available ? 'bg-zinc-600' : bm.mode === 'full' ? 'bg-emerald-400' : 'bg-amber-400'
          }`} />
          <span className="text-[12px] font-semibold text-zinc-200">{bm.name}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
            !bm.available
              ? 'bg-zinc-800 text-zinc-500'
              : bm.mode === 'full' ?'bg-emerald-950/60 text-emerald-400' :'bg-amber-950/60 text-amber-400'
          }`}>
            {!bm.available ? 'Unavailable' : bm.label}
          </span>
        </div>
        {expanded ? <ChevronUp size={12} className="text-zinc-500" /> : <ChevronDown size={12} className="text-zinc-500" />}
      </button>
      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-zinc-800/40 bg-zinc-900/30">
          <p className="text-[11px] text-zinc-400 mb-2">{bm.description}</p>
          <div className="flex flex-wrap gap-1">
            {bm.requiredChannels.map(ch => (
              <span
                key={ch}
                className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
                  bm.availableChannels.includes(ch)
                    ? 'bg-emerald-950/50 text-emerald-400 border border-emerald-800/40' :'bg-zinc-800/50 text-zinc-500 border border-zinc-700/40'
                }`}
              >
                {ch}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ChannelStatusGrid({ mappedChannels }: { mappedChannels: MappedChannel[] }) {
  return (
    <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-1.5">
      {CANONICAL_CHANNELS.map(ch => {
        const mapped = mappedChannels.find(m => m.id === ch.id);
        const available = mapped?.available ?? false;
        const status = mapped?.status ?? 'unavailable';
        const dotColor = !available
          ? 'bg-zinc-700 border-zinc-600'
          : status === 'good'   ? 'bg-emerald-500 border-emerald-700'
          : status === 'fair'   ? 'bg-amber-500 border-amber-700'
          : status === 'poor'? 'bg-red-500 border-red-700' :'bg-zinc-700 border-zinc-600';

        return (
          <div
            key={ch.id}
            title={`${ch.id} — ${available ? status : 'unavailable'} | ${ch.region}`}
            className={`flex flex-col items-center gap-1 p-1.5 rounded-lg border transition-all ${
              available
                ? 'bg-zinc-900/60 border-zinc-700/50 hover:border-zinc-600' :'bg-zinc-900/20 border-zinc-800/30 opacity-50'
            }`}
          >
            <span className={`w-2 h-2 rounded-full border ${dotColor}`} />
            <span className="text-[9px] font-mono text-zinc-400">{ch.id}</span>
            {ch.isCritical && !available && (
              <span className="text-[8px] text-red-400">!</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CoherencePanel({ pairs }: { pairs: CoherencePair[] }) {
  const available = pairs.filter(p => p.available);
  const unavailable = pairs.filter(p => !p.available);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[12px] font-semibold text-zinc-300">Inter-Hemispheric & Regional Coherence</p>
        <span className="text-[11px] text-zinc-500">{available.length}/{pairs.length} pairs active</span>
      </div>

      {available.length === 0 && (
        <div className="p-3 rounded-lg bg-zinc-900/40 border border-zinc-800/50 text-center">
          <p className="text-[11px] text-zinc-500">No coherence pairs available with current channel set</p>
        </div>
      )}

      <div className="space-y-2">
        {available.map(pair => {
          const pct = Math.round(pair.coherenceValue * 100);
          const color = pct >= 70 ? 'bg-emerald-400' : pct >= 45 ? 'bg-amber-400' : 'bg-red-400';
          const textColor = pct >= 70 ? 'text-emerald-400' : pct >= 45 ? 'text-amber-400' : 'text-red-400';
          return (
            <div key={`${pair.ch1}-${pair.ch2}`} className="flex items-center gap-3 p-2.5 rounded-lg bg-zinc-900/40 border border-zinc-800/40">
              <div className="flex items-center gap-1.5 w-28 flex-shrink-0">
                <span className="text-[10px] font-mono text-zinc-300 bg-zinc-800/60 px-1.5 py-0.5 rounded">{pair.ch1}</span>
                <span className="text-zinc-600 text-[10px]">↔</span>
                <span className="text-[10px] font-mono text-zinc-300 bg-zinc-800/60 px-1.5 py-0.5 rounded">{pair.ch2}</span>
              </div>
              <div className="flex-1">
                <p className="text-[10px] text-zinc-500 mb-1">{pair.label}</p>
                <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                  <div className={`h-full rounded-full transition-all duration-700 ${color}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
              <span className={`text-[11px] font-mono font-semibold w-10 text-right ${textColor}`}>{pair.coherenceValue.toFixed(2)}</span>
            </div>
          );
        })}
      </div>

      {unavailable.length > 0 && (
        <div className="rounded-lg border border-zinc-800/40 bg-zinc-900/20 p-2.5">
          <p className="text-[10px] text-zinc-500 mb-1.5">Unavailable pairs (missing channels):</p>
          <div className="flex flex-wrap gap-1">
            {unavailable.map(pair => (
              <span key={`${pair.ch1}-${pair.ch2}`} className="text-[10px] font-mono text-zinc-600 bg-zinc-800/40 px-1.5 py-0.5 rounded">
                {pair.ch1}↔{pair.ch2}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MultimodalSyncPanel({ report }: { report: CompatibilityReport }) {
  const syncItems = useMemo(() => buildMultimodalSyncStatus(report), [report]);

  return (
    <div className="space-y-2">
      {syncItems.map(item => (
        <div key={item.modality} className="flex items-center justify-between p-2.5 rounded-lg bg-zinc-900/40 border border-zinc-800/50">
          <div className="flex items-center gap-2.5">
            <span className="text-base">{item.icon}</span>
            <div>
              <p className="text-[12px] font-semibold text-zinc-200">{item.modality}</p>
              <p className="text-[10px] text-zinc-500">{item.note}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-right">
            <div className="text-right">
              <p className="text-[10px] font-mono text-zinc-500">{item.latency}</p>
              <p className="text-[9px] text-zinc-600">{item.sampleRate}</p>
            </div>
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
              item.status === 'synced' ? 'bg-emerald-400 animate-pulse' :
              item.status === 'degraded' ? 'bg-amber-400' : 'bg-zinc-600'
            }`} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Custom Channel Input Panel ───────────────────────────────────────────────
function CustomChannelInput({ onApply }: { onApply: (channels: Record<string, number[]>, samplingRate: number) => void }) {
  const [rawInput, setRawInput] = useState('Fz, Cz, Pz, C3, C4');
  const [samplingRate, setSamplingRate] = useState(256);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const handleApply = () => {
    try {
      const labels = rawInput.split(',').map(s => s.trim()).filter(Boolean);
      if (labels.length === 0) { setError('Enter at least one channel label'); return; }
      const channels: Record<string, number[]> = {};
      labels.forEach(label => {
        const canonical = normaliseLabel(label);
        channels[canonical] = Array.from({ length: 256 }, (_, i) =>
          Math.sin((i / 256) * 2 * Math.PI * 10) * 15 + (Math.random() - 0.5) * 5
        );
      });
      setError('');
      onApply(channels, samplingRate);
    } catch {
      setError('Invalid channel format');
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = ev.target?.result as string;
        const lines = text.split('\n').filter(Boolean);
        const headers = lines[0].split(',').map(h => h.trim());
        const channels: Record<string, number[]> = {};
        headers.forEach(h => { channels[normaliseLabel(h)] = []; });
        for (let i = 1; i < Math.min(lines.length, 513); i++) {
          const vals = lines[i].split(',');
          headers.forEach((h, idx) => {
            const v = parseFloat(vals[idx]);
            if (!isNaN(v)) channels[normaliseLabel(h)].push(v);
          });
        }
        const validChannels = Object.fromEntries(
          Object.entries(channels).filter(([, v]) => v.length > 0)
        );
        setRawInput(Object.keys(validChannels).join(', '));
        setError('');
        onApply(validChannels, samplingRate);
      } catch {
        setError('Failed to parse CSV file');
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="rounded-lg border border-zinc-800/50 p-3 bg-zinc-900/30 space-y-3">
      <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Custom Channel Input</p>
      <div className="space-y-2">
        <div>
          <label className="text-[10px] text-zinc-500 mb-1 block">Channel labels (comma-separated, 10-20 names)</label>
          <input
            type="text"
            value={rawInput}
            onChange={e => setRawInput(e.target.value)}
            placeholder="e.g. Fp1, Fp2, F3, F4, C3, C4, P3, P4"
            className="w-full bg-zinc-900/60 border border-zinc-700/50 rounded-lg px-3 py-2 text-[11px] font-mono text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-orange-500/50"
          />
        </div>
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <label className="text-[10px] text-zinc-500 mb-1 block">Sampling rate (Hz)</label>
            <input
              type="number"
              value={samplingRate}
              onChange={e => setSamplingRate(Number(e.target.value))}
              min={64} max={2048}
              className="w-full bg-zinc-900/60 border border-zinc-700/50 rounded-lg px-3 py-2 text-[11px] font-mono text-zinc-200 focus:outline-none focus:border-orange-500/50"
            />
          </div>
          <div className="flex-1">
            <label className="text-[10px] text-zinc-500 mb-1 block">Upload CSV</label>
            <button
              onClick={() => fileRef.current?.click()}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-zinc-700/50 text-zinc-400 hover:border-zinc-600 hover:text-zinc-200 transition-all text-[11px]"
            >
              <Upload size={12} />
              Browse CSV
            </button>
            <input ref={fileRef} type="file" accept=".csv,.txt" className="hidden" onChange={handleFileUpload} />
          </div>
        </div>
        {error && <p className="text-[11px] text-red-400">{error}</p>}
        <button
          onClick={handleApply}
          className="w-full py-2 rounded-lg bg-orange-500/20 border border-orange-500/40 text-orange-400 text-[12px] font-semibold hover:bg-orange-500/30 transition-all"
        >
          Apply Custom Channels
        </button>
      </div>
    </div>
  );
}

// ─── Main Module ──────────────────────────────────────────────────────────────
export default function EEGChannelAlignmentModule() {
  const [selectedDevice, setSelectedDevice] = useState<DeviceType>('8ch');
  const [activeTab, setActiveTab] = useState<'overview' | 'channels' | 'biomarkers' | 'coherence' | 'topomap' | 'multimodal' | 'testing'>('overview');
  const [isValidating, setIsValidating] = useState(false);
  const [customChannels, setCustomChannels] = useState<Record<string, number[]> | null>(null);
  const [customSamplingRate, setCustomSamplingRate] = useState(256);

  const currentInput = useMemo((): StandardisedEEGInput => {
    if (selectedDevice === 'custom' && customChannels) {
      return {
        timestamp: new Date().toISOString(),
        channels: customChannels,
        sampling_rate: customSamplingRate,
        device_type: 'custom',
      };
    }
    return TEST_DATASETS[selectedDevice];
  }, [selectedDevice, customChannels, customSamplingRate]);

  const mappedChannels = useMemo(
    () => mapChannelsToCanonical(currentInput.channels, selectedDevice),
    [currentInput, selectedDevice]
  );

  const report = useMemo(
    () => validateCompatibility(currentInput, mappedChannels),
    [currentInput, mappedChannels]
  );

  const biomarkers = useMemo(
    () => computeBiomarkerAdaptations(mappedChannels, report.resolutionMode),
    [mappedChannels, report.resolutionMode]
  );

  const coherencePairs = useMemo(
    () => computeCoherencePairs(mappedChannels),
    [mappedChannels]
  );

  const aiContext = useMemo(
    () => buildAIChannelContext(report, mappedChannels),
    [report, mappedChannels]
  );

  const handleRevalidate = useCallback(() => {
    setIsValidating(true);
    setTimeout(() => setIsValidating(false), 800);
  }, []);

  const handleCustomApply = useCallback((channels: Record<string, number[]>, sr: number) => {
    setCustomChannels(channels);
    setCustomSamplingRate(sr);
  }, []);

  const tabs = [
    { id: 'overview',   label: 'Overview',   icon: <BarChart2 size={13} /> },
    { id: 'channels',   label: 'Channels',   icon: <Cpu size={13} /> },
    { id: 'biomarkers', label: 'Biomarkers', icon: <Activity size={13} /> },
    { id: 'coherence',  label: 'Coherence',  icon: <GitBranch size={13} /> },
    { id: 'topomap',    label: 'Topomap',    icon: <Layers size={13} /> },
    { id: 'multimodal', label: 'Multimodal', icon: <Zap size={13} /> },
    { id: 'testing',    label: 'Testing',    icon: <RefreshCw size={13} /> },
  ] as const;

  // Compute test comparison data once
  const testComparison = useMemo(() => {
    const datasets: DeviceType[] = ['8ch', 'DSI-24', 'custom'];
    return datasets.map(dt => {
      const input = TEST_DATASETS[dt];
      const mapped = mapChannelsToCanonical(input.channels, dt);
      const rep = validateCompatibility(input, mapped);
      const bms = computeBiomarkerAdaptations(mapped, rep.resolutionMode);
      const fullBms = bms.filter(b => b.available && b.mode === 'full').length;
      const reducedBms = bms.filter(b => b.available && b.mode === 'reduced').length;
      return { dt, rep, fullBms, reducedBms };
    });
  }, []);

  return (
    <div className="rounded-xl border border-zinc-800/60 bg-zinc-900/40 overflow-hidden">
      {/* ── Header ── */}
      <div className="px-4 py-3.5 border-b border-zinc-800/60 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-8 h-8 rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center flex-shrink-0">
            <Cpu size={16} className="text-orange-400" />
          </div>
          <div>
            <p className="text-[14px] font-semibold text-zinc-100">EEG Channel Alignment & Validation</p>
            <p className="text-[11px] text-zinc-500">10-20 system standardisation · multi-device compatibility</p>
          </div>
        </div>

        {/* Device selector */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] text-zinc-500 hidden sm:inline">Device:</span>
          {(['8ch', 'DSI-24', 'custom'] as DeviceType[]).map(d => (
            <button
              key={d}
              onClick={() => setSelectedDevice(d)}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold border transition-all ${
                selectedDevice === d
                  ? 'bg-orange-500/20 border-orange-500/50 text-orange-400' :'bg-zinc-900/60 border-zinc-700/50 text-zinc-400 hover:border-zinc-600'
              }`}
            >
              {d}
            </button>
          ))}
          <button
            onClick={handleRevalidate}
            disabled={isValidating}
            className="p-1.5 rounded-lg border border-zinc-700/50 text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 transition-all"
            title="Re-validate"
          >
            <RefreshCw size={13} className={isValidating ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* ── Status Bar ── */}
      <div className="px-4 py-2 border-b border-zinc-800/40 bg-zinc-900/20 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-1.5">
          <Radio size={11} className={`${report.samplingRateValid ? 'text-emerald-400' : 'text-red-400'}`} />
          <span className="text-[11px] text-zinc-400">
            <span className="font-mono font-semibold text-zinc-200">{report.samplingRate} Hz</span>
            <span className="ml-1 text-zinc-600">sample rate</span>
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`w-1.5 h-1.5 rounded-full ${report.resolutionMode === 'full' ? 'bg-emerald-400' : 'bg-amber-400'}`} />
          <span className="text-[11px] text-zinc-400">
            {report.resolutionMode === 'full' ? 'Full-resolution analysis' : 'Reduced-resolution analysis'}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] text-zinc-400">
            <span className="font-mono font-semibold text-zinc-200">{report.availableChannels}</span>
            <span className="text-zinc-600">/{CANONICAL_CHANNELS.length} channels</span>
          </span>
        </div>
        <div className="ml-auto">
          <ReliabilityBadge reliability={report.biomarkerReliability} />
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="flex overflow-x-auto border-b border-zinc-800/60 scrollbar-thin">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-[12px] font-medium whitespace-nowrap border-b-2 transition-all ${
              activeTab === tab.id
                ? 'border-orange-500 text-orange-400' :'border-transparent text-zinc-500 hover:text-zinc-300'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Tab Content ── */}
      <div className="p-4">

        {/* OVERVIEW */}
        {activeTab === 'overview' && (
          <div className="space-y-4">
            {/* Score + summary row */}
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <ScoreBadge score={report.score} />
              <div className="flex-1 space-y-2">
                <div className="flex flex-wrap gap-2 items-center">
                  <span className="text-[13px] font-semibold text-zinc-200">
                    {selectedDevice} — {report.availableChannels} channels active
                  </span>
                  <ReliabilityBadge reliability={report.biomarkerReliability} />
                  <span className={`text-[11px] px-2 py-0.5 rounded-full border font-medium ${
                    report.resolutionMode === 'full' ?'bg-emerald-950/40 border-emerald-800/50 text-emerald-400' :'bg-amber-950/40 border-amber-800/50 text-amber-400'
                  }`}>
                    {report.resolutionMode === 'full' ? 'Full-resolution analysis' : 'Reduced-resolution analysis'}
                  </span>
                </div>
                <div className="flex flex-wrap gap-3 text-[11px] text-zinc-400">
                  <span>Sampling rate: <span className={`font-mono font-semibold ${report.samplingRateValid ? 'text-emerald-400' : 'text-red-400'}`}>{report.samplingRate} Hz</span></span>
                  <span>Critical missing: <span className={`font-semibold ${report.criticalMissing.length > 0 ? 'text-red-400' : 'text-emerald-400'}`}>{report.criticalMissing.length}</span></span>
                  <span>Warnings: <span className={`font-semibold ${report.warnings.length > 0 ? 'text-amber-400' : 'text-zinc-400'}`}>{report.warnings.length}</span></span>
                </div>
              </div>
            </div>

            {/* Spatial coverage */}
            <div className="rounded-lg border border-zinc-800/50 p-3 bg-zinc-900/30">
              <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wide mb-3">Spatial Coverage by Region</p>
              <div className="space-y-2">
                {(Object.entries(report.spatialCoverage) as [EEGRegion, number][]).map(([region, cov]) => (
                  <CoverageBar key={region} region={region} coverage={cov} />
                ))}
              </div>
            </div>

            {/* Warnings & Errors */}
            {(report.warnings.length > 0 || report.errors.length > 0) && (
              <div className="space-y-2">
                {report.errors.map((err, i) => (
                  <div key={`err-${i}`} className="flex items-start gap-2 p-2.5 rounded-lg bg-red-950/20 border border-red-800/40">
                    <XCircle size={13} className="text-red-400 flex-shrink-0 mt-0.5" />
                    <span className="text-[11px] text-red-300">{err}</span>
                  </div>
                ))}
                {report.warnings.map((warn, i) => (
                  <div key={`warn-${i}`} className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-950/20 border border-amber-800/40">
                    <AlertTriangle size={13} className="text-amber-400 flex-shrink-0 mt-0.5" />
                    <span className="text-[11px] text-amber-300">{warn}</span>
                  </div>
                ))}
              </div>
            )}

            {/* AI context */}
            <div className="rounded-lg border border-zinc-800/50 p-3 bg-zinc-900/30">
              <div className="flex items-center gap-2 mb-2">
                <Info size={13} className="text-sky-400" />
                <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">AI Interpretation Context</span>
              </div>
              <p className="text-[11px] text-zinc-400 leading-relaxed">{aiContext}</p>
            </div>

            {/* Biomarker summary */}
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: 'Full-res biomarkers', value: biomarkers.filter(b => b.available && b.mode === 'full').length, color: 'text-emerald-400' },
                { label: 'Reduced biomarkers', value: biomarkers.filter(b => b.available && b.mode === 'reduced').length, color: 'text-amber-400' },
                { label: 'Unavailable', value: biomarkers.filter(b => !b.available).length, color: 'text-zinc-500' },
              ].map(item => (
                <div key={item.label} className="p-2.5 rounded-lg bg-zinc-900/40 border border-zinc-800/40 text-center">
                  <p className={`text-xl font-bold font-mono ${item.color}`}>{item.value}</p>
                  <p className="text-[10px] text-zinc-500 mt-0.5">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CHANNELS */}
        {activeTab === 'channels' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-[12px] font-semibold text-zinc-300">
                10-20 System Channel Map — {report.availableChannels}/{CANONICAL_CHANNELS.length} active
              </p>
              <div className="flex items-center gap-3 text-[10px] text-zinc-500">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" />Good</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" />Fair</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" />Poor</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-zinc-700" />Unavailable</span>
              </div>
            </div>
            <ChannelStatusGrid mappedChannels={mappedChannels} />

            {/* Detailed table */}
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="border-b border-zinc-800/60">
                    {['Channel', 'Region', 'Hemisphere', 'SQI', 'Impedance (kΩ)', 'Amplitude (µV)', 'Status', 'Critical'].map(h => (
                      <th key={h} className="text-left pb-2 pr-3 font-medium text-zinc-500 uppercase tracking-wide text-[10px] whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {mappedChannels.filter(c => c.available).map(ch => (
                    <tr key={ch.id} className="border-b border-zinc-800/30 hover:bg-zinc-800/20 transition-colors">
                      <td className="py-2 pr-3 font-mono font-semibold text-zinc-200">{ch.id}</td>
                      <td className="py-2 pr-3 text-zinc-400 capitalize">{ch.region}</td>
                      <td className="py-2 pr-3 text-zinc-500 capitalize">{ch.hemisphere}</td>
                      <td className="py-2 pr-3">
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono text-zinc-300">{ch.signalQuality}</span>
                          <div className="w-12 h-1 rounded-full bg-zinc-800 overflow-hidden">
                            <div
                              className={`h-full rounded-full ${ch.status === 'good' ? 'bg-emerald-400' : ch.status === 'fair' ? 'bg-amber-400' : 'bg-red-400'}`}
                              style={{ width: `${ch.signalQuality}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="py-2 pr-3 font-mono text-zinc-300">{ch.impedance.toFixed(1)}</td>
                      <td className="py-2 pr-3 font-mono text-zinc-300">{ch.amplitude.toFixed(1)}</td>
                      <td className="py-2 pr-3">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${
                          ch.status === 'good' ? 'bg-emerald-950/60 text-emerald-400' :
                          ch.status === 'fair'? 'bg-amber-950/60 text-amber-400' : 'bg-red-950/60 text-red-400'
                        }`}>{ch.status}</span>
                      </td>
                      <td className="py-2 pr-3">
                        {ch.isCritical && <span className="text-orange-400 text-[10px] font-semibold">✓</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Missing critical channels */}
            {report.criticalMissing.length > 0 && (
              <div className="rounded-lg border border-red-800/40 bg-red-950/20 p-3">
                <p className="text-[11px] font-semibold text-red-400 mb-2">Missing Critical Channels</p>
                <div className="flex flex-wrap gap-1.5">
                  {report.criticalMissing.map(ch => (
                    <span key={ch} className="px-2 py-0.5 rounded bg-red-950/60 border border-red-800/50 text-red-300 text-[11px] font-mono">{ch}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Custom input for custom device */}
            {selectedDevice === 'custom' && (
              <CustomChannelInput onApply={handleCustomApply} />
            )}
          </div>
        )}

        {/* BIOMARKERS */}
        {activeTab === 'biomarkers' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[12px] font-semibold text-zinc-300">Feature Adaptation Layer</p>
              <div className="flex items-center gap-2 text-[10px]">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400" />Full-res</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" />Reduced</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-zinc-600" />Unavailable</span>
              </div>
            </div>

            {/* Resolution mode banner */}
            <div className={`p-2.5 rounded-lg border text-[11px] ${
              report.resolutionMode === 'full' ?'bg-emerald-950/20 border-emerald-800/40 text-emerald-300' :'bg-amber-950/20 border-amber-800/40 text-amber-300'
            }`}>
              {report.resolutionMode === 'full' ?'✓ Full-resolution analysis active — all biomarkers use complete spatial models with regional comparisons and coherence' :'⚠ Reduced-resolution analysis — biomarkers use regional averages; coherence limited to available electrode pairs'}
            </div>

            {biomarkers.map(bm => (
              <BiomarkerRow key={bm.name} bm={bm} />
            ))}
          </div>
        )}

        {/* COHERENCE */}
        {activeTab === 'coherence' && (
          <div className="space-y-4">
            <CoherencePanel pairs={coherencePairs} />

            {report.resolutionMode === 'reduced' && (
              <div className="rounded-lg border border-amber-800/40 bg-amber-950/20 p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle size={13} className="text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[11px] font-semibold text-amber-400 mb-1">Reduced Coherence Analysis</p>
                    <p className="text-[11px] text-amber-300/80">
                      8-channel systems limit coherence to available electrode pairs only. Full inter-hemispheric coherence mapping requires DSI-24 or equivalent high-density system with temporal (T7/T8) and occipital (O1/O2) coverage.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="rounded-lg border border-zinc-800/50 p-3 bg-zinc-900/30">
              <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wide mb-2">Coherence Summary</p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: 'Active pairs', value: coherencePairs.filter(p => p.available).length, color: 'text-emerald-400' },
                  { label: 'High coherence', value: coherencePairs.filter(p => p.available && p.coherenceValue >= 0.7).length, color: 'text-sky-400' },
                  { label: 'Unavailable', value: coherencePairs.filter(p => !p.available).length, color: 'text-zinc-500' },
                ].map(item => (
                  <div key={item.label} className="p-2 rounded bg-zinc-900/60 border border-zinc-800/40 text-center">
                    <p className={`text-lg font-bold font-mono ${item.color}`}>{item.value}</p>
                    <p className="text-[10px] text-zinc-500">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* TOPOMAP */}
        {activeTab === 'topomap' && (
          <div className="flex flex-col lg:flex-row gap-6 items-start">
            <div className="flex-shrink-0">
              <EEGTopoMap mappedChannels={mappedChannels} report={report} size={300} animated />
            </div>
            <div className="flex-1 space-y-4">
              <div className="rounded-lg border border-zinc-800/50 p-3 bg-zinc-900/30">
                <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wide mb-2">Topomap Mode</p>
                <p className="text-[12px] text-zinc-300">
                  {report.resolutionMode === 'full' ?'Full-density topomap with complete 10-20 coverage. All regions represented with accurate spatial interpolation.' :'Sparse topomap — only available electrodes shown. No interpolation applied. Missing regions are marked as unavailable.'}
                </p>
              </div>
              <div className="space-y-2">
                <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Regional Coverage</p>
                {(Object.entries(report.spatialCoverage) as [EEGRegion, number][]).map(([region, cov]) => (
                  <CoverageBar key={region} region={region} coverage={cov} />
                ))}
              </div>
              {report.resolutionMode === 'reduced' && (
                <div className="rounded-lg border border-amber-800/40 bg-amber-950/20 p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={13} className="text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[11px] font-semibold text-amber-400 mb-1">Limited Spatial Resolution</p>
                      <p className="text-[11px] text-amber-300/80">
                        8-channel systems provide regional averages only. Topographic gradients between adjacent electrodes cannot be resolved. Upgrade to DSI-24 for full spatial analysis.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* MULTIMODAL */}
        {activeTab === 'multimodal' && (
          <div className="space-y-4">
            <p className="text-[12px] font-semibold text-zinc-300">Multimodal Time Synchronisation</p>
            <MultimodalSyncPanel report={report} />

            <div className="rounded-lg border border-zinc-800/50 p-3 bg-zinc-900/30">
              <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wide mb-2">Standardised Data Model</p>
              <pre className="text-[10px] font-mono text-zinc-400 overflow-x-auto leading-relaxed whitespace-pre-wrap">
{`{
  "timestamp": "${new Date().toISOString()}",
  "channels": {
${Object.keys(currentInput.channels).slice(0, 4).map(ch => `    "${ch}": [/* ${currentInput.channels[ch].length} samples */]`).join(',\n')}
    ...
  },
  "sampling_rate": ${currentInput.sampling_rate},
  "device_type": "${currentInput.device_type}"
}`}
              </pre>
            </div>

            <div className="rounded-lg border border-zinc-800/50 p-3 bg-zinc-900/30">
              <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wide mb-2">Cross-Modal Analysis</p>
              <div className="space-y-2 text-[11px] text-zinc-400">
                <div className="flex items-start gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>EEG ↔ PPG: Cognitive load vs HRV correlation — theta power inversely correlated with HRV coherence</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>EEG ↔ Wearable Accelerometer: Motion artefact cross-reference — movement epochs flagged and excluded from analysis</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className={`w-2 h-2 rounded-full mt-0.5 flex-shrink-0 ${report.samplingRateValid ? 'bg-emerald-400' : 'bg-amber-400'}`} />
                  <span>Timestamp alignment: {report.samplingRate} Hz EEG ↔ wearable stream — shared Unix epoch synchronisation</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className={`w-2 h-2 rounded-full mt-0.5 flex-shrink-0 ${report.availableChannels >= 8 ? 'bg-emerald-400' : 'bg-amber-400'}`} />
                  <span>EEG ↔ SpO₂: Posterior alpha suppression correlated with oxygen saturation changes</span>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-zinc-800/50 p-3 bg-zinc-900/30">
              <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wide mb-2">Sync Architecture</p>
              <div className="flex items-center gap-2 text-[10px] text-zinc-500 flex-wrap">
                <span className="px-2 py-1 rounded bg-zinc-800/60 border border-zinc-700/40 font-mono">EEG ({report.samplingRate} Hz)</span>
                <span>→</span>
                <span className="px-2 py-1 rounded bg-zinc-800/60 border border-zinc-700/40 font-mono">Timestamp Aligner</span>
                <span>→</span>
                <span className="px-2 py-1 rounded bg-zinc-800/60 border border-zinc-700/40 font-mono">PPG (64 Hz)</span>
                <span>+</span>
                <span className="px-2 py-1 rounded bg-zinc-800/60 border border-zinc-700/40 font-mono">Wearable (50 Hz)</span>
                <span>→</span>
                <span className="px-2 py-1 rounded bg-orange-950/40 border border-orange-800/40 font-mono text-orange-400">Cross-Modal Analysis</span>
              </div>
            </div>
          </div>
        )}

        {/* TESTING */}
        {activeTab === 'testing' && (
          <div className="space-y-4">
            <p className="text-[12px] font-semibold text-zinc-300">Validation Test Cases — System Comparison</p>

            {/* Comparison table */}
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="border-b border-zinc-800/60">
                    {['Metric', '8-channel', 'DSI-24', 'Custom (3ch)'].map(h => (
                      <th key={h} className="text-left pb-2 pr-4 font-medium text-zinc-500 uppercase tracking-wide text-[10px]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      metric: 'Active Channels',
                      values: testComparison.map(t => `${t.rep.availableChannels}`),
                      colors: ['text-amber-300', 'text-emerald-300', 'text-red-300'],
                    },
                    {
                      metric: 'Sampling Rate',
                      values: testComparison.map(t => `${t.rep.samplingRate} Hz`),
                      colors: ['text-amber-300', 'text-emerald-300', 'text-amber-300'],
                    },
                    {
                      metric: 'Resolution Mode',
                      values: testComparison.map(t => t.rep.resolutionMode === 'full' ? 'Full' : 'Reduced'),
                      colors: ['text-amber-300', 'text-emerald-300', 'text-red-300'],
                    },
                    {
                      metric: 'Compat. Score',
                      values: testComparison.map(t => `${t.rep.score}/100`),
                      colors: ['text-amber-300', 'text-emerald-300', 'text-red-300'],
                    },
                    {
                      metric: 'Biomarker Reliability',
                      values: testComparison.map(t => t.rep.biomarkerReliability.charAt(0).toUpperCase() + t.rep.biomarkerReliability.slice(1)),
                      colors: ['text-amber-300', 'text-emerald-300', 'text-red-300'],
                    },
                    {
                      metric: 'Full-res Biomarkers',
                      values: testComparison.map(t => `${t.fullBms}`),
                      colors: ['text-amber-300', 'text-emerald-300', 'text-red-300'],
                    },
                    {
                      metric: 'Topomap Type',
                      values: ['Sparse', 'Full', 'Sparse'],
                      colors: ['text-amber-300', 'text-emerald-300', 'text-red-300'],
                    },
                    {
                      metric: 'Frontal Coverage',
                      values: testComparison.map(t => `${Math.round(t.rep.spatialCoverage.frontal * 100)}%`),
                      colors: ['text-amber-300', 'text-emerald-300', 'text-red-300'],
                    },
                    {
                      metric: 'Parietal Coverage',
                      values: testComparison.map(t => `${Math.round(t.rep.spatialCoverage.parietal * 100)}%`),
                      colors: ['text-amber-300', 'text-emerald-300', 'text-red-300'],
                    },
                    {
                      metric: 'Critical Missing',
                      values: testComparison.map(t => `${t.rep.criticalMissing.length}`),
                      colors: ['text-amber-300', 'text-emerald-300', 'text-red-300'],
                    },
                  ].map(row => (
                    <tr key={row.metric} className="border-b border-zinc-800/30 hover:bg-zinc-800/20 transition-colors">
                      <td className="py-2 pr-4 text-zinc-400 font-medium">{row.metric}</td>
                      {row.values.map((val, i) => (
                        <td key={i} className={`py-2 pr-4 font-mono ${row.colors[i]}`}>{val}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* AI interpretation comparison */}
            <div className="space-y-2">
              <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">AI Interpretation Examples</p>
              {testComparison.map(({ dt, rep }) => {
                const mapped = mapChannelsToCanonical(TEST_DATASETS[dt].channels, dt);
                const ctx = buildAIChannelContext(rep, mapped);
                return (
                  <div key={dt} className="p-2.5 rounded-lg bg-zinc-900/40 border border-zinc-800/40">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded font-mono ${
                        dt === 'DSI-24' ? 'bg-emerald-950/60 text-emerald-400' :
                        dt === '8ch'? 'bg-amber-950/60 text-amber-400' : 'bg-red-950/60 text-red-400'
                      }`}>{dt}</span>
                      <span className="text-[10px] text-zinc-500">Score: {rep.score}/100</span>
                    </div>
                    <p className="text-[11px] text-zinc-400 leading-relaxed italic">&ldquo;{ctx}&rdquo;</p>
                  </div>
                );
              })}
            </div>

            {/* Currently active test */}
            <div className="rounded-lg border border-zinc-800/50 p-3 bg-zinc-900/30">
              <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wide mb-2">
                Active Test: {selectedDevice}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {[
                  { label: 'Channels', value: `${report.availableChannels}` },
                  { label: 'Score', value: `${report.score}/100` },
                  { label: 'Sampling Rate', value: `${report.samplingRate} Hz` },
                  { label: 'Resolution', value: report.resolutionMode },
                  { label: 'Reliability', value: report.biomarkerReliability },
                  { label: 'Warnings', value: `${report.warnings.length}` },
                ].map(item => (
                  <div key={item.label} className="p-2 rounded bg-zinc-900/60 border border-zinc-800/40">
                    <p className="text-[10px] text-zinc-500 uppercase tracking-wide">{item.label}</p>
                    <p className="text-[13px] font-semibold font-mono text-zinc-200 mt-0.5">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
